import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import { useParams, useHistory } from "react-router-dom";
import FiveSlotMachineDetailCard from "./components/FiveSlotMachineDetailCard";
import { DUMMY_LOTTERIES } from "./FiveSlotLottery";
import NavBar from "../../../Components/NavBar";
import { connectWallet as triggerConnectWallet } from "../../../../static/walletUtils";
import { Path } from "../../../../static/Constants";
import { colors, borderRadiusPx, customShadows } from "../../../../theme";
import { CommonBreadcrumbs, CommonButton } from "../../../Components/ui";
import { FIVE_SLOT_DETAILS_THEME } from "./components/fiveSlotConfig";
import { toast } from "react-toastify";

import slotInstance, { ca as slotContractAddress } from "../../../Service/slotInstance";
import tokenInstance from "../../../Service/tokencinstance";
import web3 from "../../../Service/web3";

const FiveSlotLotteryDetails = () => {
  const { id } = useParams();
  const history = useHistory();

    const [spinLoading, setSpinLoading] = useState(false);
    const [lastSpinResult, setLastSpinResult] = useState(null);

  const [account, setAccount] = useState("");
  const [isCheckingWallet, setIsCheckingWallet] = useState(true);

  // User-specific values
  const [balance, setBalance] = useState("1250.00");
  const [win, setWin] = useState("0.00");

  // Find the lottery using the ID from the URL or contract
  const [lotteryData] = useState(() => {
    const found = DUMMY_LOTTERIES.find((lottery) => lottery.lotteryId.toString() === id);
    return (
      found || {
        lotteryId: id || 201,
        name: `5-SlotBET #${id || 201}`,
        creator: "AutoBET Official",
        slotCategory: "fiat",
        currency: "BET",
        jackpots: {
          mini: { amount: "2840.50", seedValue: "2000.00", contributionPerSpin: "0.030" },
          major: { amount: "16450.00", seedValue: "10000.00", contributionPerSpin: "0.012" },
          mega: { amount: "128900.00", seedValue: "50000.00", contributionPerSpin: "0.005" },
        },
      }
    );
  });

  // Check whether a wallet is already connected
  useEffect(() => {
    const checkWalletConnection = async () => {
      if (!window.ethereum) {
        setIsCheckingWallet(false);
        return;
      }

      try {
        const accounts = await window.ethereum.request({
          method: "eth_accounts",
        });

        if (accounts && accounts.length > 0) {
          setAccount(accounts[0]);
        }
      } catch (error) {
        console.error("Failed to check wallet connection:", error);
      } finally {
        setIsCheckingWallet(false);
      }
    };

    checkWalletConnection();
  }, []);

  // Listen for wallet changes
  useEffect(() => {
    if (!window.ethereum) return;

    const handleAccountsChanged = (accounts) => {
      setAccount(accounts?.[0] || "");
    };

    window.ethereum.on("accountsChanged", handleAccountsChanged);

    return () => {
      window.ethereum.removeListener("accountsChanged", handleAccountsChanged);
    };
  }, []);

  const handleConnectWallet = async () => {
    const res = await triggerConnectWallet();
    if (res.account) {
      setAccount(res.account);
    }
  };

  if (isCheckingWallet) {
    return null;
  }

    const waitForSpinResult = async (requestId) => {
        for (let i = 0; i < 20; i++) {
            const result = await slotInstance.methods.getSpinResult(requestId).call();

            if (result?.fulfilled) {
                const reels = await slotInstance.methods.getReels5(requestId).call();

                return {
                    requestId,
                    result,
                    reels: [
                        Number(reels.s0 ?? reels[0]),
                        Number(reels.s1 ?? reels[1]),
                        Number(reels.s2 ?? reels[2]),
                        Number(reels.s3 ?? reels[3]),
                        Number(reels.s4 ?? reels[4]),
                    ],
                    payout: web3.utils.fromWei(String(result.payout || "0"), "ether"),
                };
            }

            await new Promise((resolve) => setTimeout(resolve, 3000));
        }

        throw new Error("Spin result is still pending. Please check again shortly.");
    };

    const handleSpin5Reel = async (lotteryData, betAmount) => {
        try {
            if (!window.ethereum) {
                toast.error("Web3 wallet not detected.");
                return null;
            }

            if (!slotInstance?.methods?.spin5Reel) {
                toast.error("spin5Reel method not found on slot contract.");
                return null;
            }

            setSpinLoading(true);

            const accounts = await web3.eth.getAccounts();

            if (!accounts || accounts.length === 0) {
                toast.error("Please connect your wallet first.");
                return null;
            }

            const activeAccount = accounts[0];
            const gameId = lotteryData?.gameId || lotteryData?.lotteryId;

            if (!gameId) {
                toast.error("Invalid slot game.");
                return null;
            }

            if (!betAmount || Number(betAmount) <= 0) {
                toast.error("Invalid bet amount.");
                return null;
            }

            const betAmountWei = web3.utils.toWei(String(betAmount), "ether");

            const minBetWei = lotteryData?.minBet
                ? web3.utils.toWei(String(lotteryData.minBet), "ether")
                : "0";

            const maxBetWei = lotteryData?.maxBet
                ? web3.utils.toWei(String(lotteryData.maxBet), "ether")
                : "0";

            if (web3.utils.toBN(betAmountWei).lt(web3.utils.toBN(minBetWei))) {
                toast.error(`Minimum bet is ${lotteryData.minBet}.`);
                return null;
            }

            if (
                maxBetWei !== "0" &&
                web3.utils.toBN(betAmountWei).gt(web3.utils.toBN(maxBetWei))
            ) {
                toast.error(`Maximum bet is ${lotteryData.maxBet}.`);
                return null;
            }

            toast.info("Approving BET tokens...");

            const currentAllowance = await tokenInstance.methods
                .allowance(activeAccount, slotContractAddress)
                .call();

            if (web3.utils.toBN(currentAllowance).lt(web3.utils.toBN(betAmountWei))) {
                await tokenInstance.methods
                    .approve(slotContractAddress, betAmountWei)
                    .send({ from: activeAccount });
            }

            toast.info("Sending spin transaction...");

            const receipt = await slotInstance.methods
                .spin5Reel(gameId, betAmountWei)
                .send({
                    from: activeAccount,
                    gas: 1000000,
                });

            console.log("spin5Reel receipt:", receipt);

            const requestId =
                receipt?.events?.SlotSpinRequested?.returnValues?.requestId ||
                receipt?.events?.SlotSpinRequested?.returnValues?.[2];

            if (!requestId) {
                toast.success("Spin transaction submitted.");
                return {
                    receipt,
                    requestId: null,
                    pending: true,
                };
            }

            toast.info("Spin requested. Waiting for result...");

            const spinResult = await waitForSpinResult(requestId);

            setLastSpinResult(spinResult);

            if (Number(spinResult.payout) > 0) {
                toast.success(`You won ${spinResult.payout} BET!`);
            } else {
                toast.info("No win this spin. Try again!");
            }

            return spinResult;
        } catch (error) {
            console.error("spin5Reel error:", error);

            if (error?.code === 4001) {
                toast.error("Transaction rejected.");
            } else {
                toast.error(error?.message || "Spin failed.");
            }

            return null;
        } finally {
            setSpinLoading(false);
        }
    };

  return (
    <Box
      sx={{
        position: "relative",
        overflowX: "hidden",
        minHeight: "100vh",
        bgcolor: FIVE_SLOT_DETAILS_THEME.pageBg || colors.dashboard.canvas,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <NavBar />
      <Container
        maxWidth={FIVE_SLOT_DETAILS_THEME.containerMaxWidth || "lg"}
        className="slot-page-container"
        sx={{
          pt: { xs: "90px", sm: "100px", md: "108px" },
          pb: { xs: 4, md: 8 },
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: FIVE_SLOT_DETAILS_THEME.cardMaxWidth || 590,
            mb: 2.5,
          }}
        >
          {/* Breadcrumbs Navigation */}
          <CommonBreadcrumbs
            items={[
              { label: "Home", path: Path.root },
              { label: "5-SlotBET", path: Path.FiveSlotlottery || "/five-slotlottery" },
              { label: lotteryData.name || `5-SlotBET #${id || 201}` },
            ]}
          />

          {/* Action Row */}
          <Box
            sx={{
              mt: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <CommonButton
              variant="refresh"
              size="small"
              startIcon={<ArrowBackIcon />}
              onClick={() => history.push(Path.FiveSlotlottery || "/five-slotlottery")}
            >
              Back to Arena
            </CommonButton>

            {!account && (
              <CommonButton
                variant="primary"
                size="small"
                startIcon={<AccountBalanceWalletIcon />}
                onClick={handleConnectWallet}
              >
                Connect Wallet
              </CommonButton>
            )}
          </Box>
        </Box>

        {/* The 5-Slot Pokies Mobile-first Interface */}
        <Box sx={{ width: "100%", maxWidth: FIVE_SLOT_DETAILS_THEME.cardMaxWidth || 590 }}>
          <FiveSlotMachineDetailCard
            lotteryData={lotteryData}
            account={account}
            onSpin={handleSpin5Reel}
            spinLoading={spinLoading}
            lastSpinResult={lastSpinResult}
            balance={balance}
            win={win}
            setBalance={setBalance}
            setWin={setWin}
          />
        </Box>
      </Container>
    </Box>
  );
};

export default FiveSlotLotteryDetails;
